import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  CardActions,
  InputAdornment,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  Remove as RemoveIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  OpenInNew as OpenInNewIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { adminApi } from '../../services/adminApi';

interface User {
  id: number;
  email: string;
}

interface Permission {
  id: number;
  name: string;
  description: string;
}

interface Group {
  id: number;
  name: string;
  users?: User[];
  permissions?: Permission[];
}

const Groups: React.FC = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState<Group[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [formData, setFormData] = useState({ name: '' });
  
  // New state for relationship management
  const [selectedUser, setSelectedUser] = useState<number | ''>('');
  const [selectedPermission, setSelectedPermission] = useState<number | ''>('');
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  
  // New state for multiple permission selection
  const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);
  const [openMultiplePermissionsDialog, setOpenMultiplePermissionsDialog] = useState(false);
  const [currentGroupForMultiplePermissions, setCurrentGroupForMultiplePermissions] = useState<number | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      console.log('Fetching groups, users, and permissions...');
      
      const [groupsResponse, usersResponse, permissionsResponse] = await Promise.all([
        adminApi.getGroups(),
        adminApi.getUsers(),
        adminApi.getPermissions()
      ]);

      console.log('Groups response:', groupsResponse);
      console.log('Users response:', usersResponse);
      console.log('Permissions response:', permissionsResponse);

      if (groupsResponse.success) {
        console.log('Setting groups:', groupsResponse.data);
        setGroups(groupsResponse.data);
        
        // Check if we need to expand a specific group (navigated from Users)
        const expandGroupId = sessionStorage.getItem('expandGroupId');
        if (expandGroupId) {
          const groupId = parseInt(expandGroupId);
          setExpandedGroups(new Set([groupId]));
          // Clear the sessionStorage
          sessionStorage.removeItem('expandGroupId');
        }
      }
      if (usersResponse.success) {
        console.log('Setting users:', usersResponse.data);
        setUsers(usersResponse.data);
      }
      if (permissionsResponse.success) {
        console.log('Setting permissions:', permissionsResponse.data);
        setPermissions(permissionsResponse.data);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Error loading data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenDialog = (group?: Group) => {
    if (group) {
      setEditingGroup(group);
      setFormData({ name: group.name });
    } else {
      setEditingGroup(null);
      setFormData({ name: '' });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingGroup(null);
    setFormData({ name: '' });
  };

  const handleSubmit = async () => {
    try {
      if (editingGroup) {
        // Update group
        const response = await adminApi.updateGroup(editingGroup.id, formData);
        if (response.success) {
          setGroups(groups.map(g => g.id === editingGroup.id ? response.data : g));
          handleCloseDialog();
          setSuccess('Group updated successfully');
        } else {
          setError('Failed to update group');
        }
      } else {
        // Create group
        const response = await adminApi.createGroup(formData);
        if (response.success) {
          setGroups([...groups, response.data]);
          handleCloseDialog();
          setSuccess('Group created successfully');
        } else {
          setError('Failed to create group');
        }
      }
    } catch (err) {
      setError('Error saving group');
    }
  };

  const handleDelete = async (groupId: number) => {
    if (window.confirm('Are you sure you want to delete this group?')) {
      try {
        const response = await adminApi.deleteGroup(groupId);
        if (response.success) {
          setGroups(groups.filter(g => g.id !== groupId));
          setSuccess('Group deleted successfully');
        } else {
          setError('Failed to delete group');
        }
      } catch (err) {
        setError('Error deleting group');
      }
    }
  };

  const handleAddUserToGroup = async (groupId: number) => {
    if (!selectedUser) {
      setError('Please select a user');
      return;
    }

    try {
      const response = await adminApi.addUserToGroup(groupId, selectedUser);
      if (response.success) {
        // Refresh groups to get updated data
        const groupsResponse = await adminApi.getGroups();
        if (groupsResponse.success) {
          setGroups(groupsResponse.data);
        }
        setSelectedUser('' as number | '');
        setSuccess('User added to group successfully');
      } else {
        setError('Failed to add user to group');
      }
    } catch (err) {
      setError('Error adding user to group');
    }
  };

  const handleRemoveUserFromGroup = async (groupId: number, userId: number) => {
    try {
      const response = await adminApi.removeUserFromGroup(groupId, userId);
      if (response.success) {
        // Refresh groups to get updated data
        const groupsResponse = await adminApi.getGroups();
        if (groupsResponse.success) {
          setGroups(groupsResponse.data);
        }
        setSuccess('User removed from group successfully');
      } else {
        setError('Failed to remove user from group');
      }
    } catch (err) {
      setError('Error removing user from group');
    }
  };

  const handleAddPermissionToGroup = async (groupId: number) => {
    if (!selectedPermission) {
      setError('Please select a permission');
      return;
    }

    try {
      const response = await adminApi.addPermissionToGroup(groupId, selectedPermission);
      if (response.success) {
        // Refresh groups to get updated data
        const groupsResponse = await adminApi.getGroups();
        if (groupsResponse.success) {
          setGroups(groupsResponse.data);
        }
        setSelectedPermission('' as number | '');
        setSuccess('Permission added to group successfully');
      } else {
        setError('Failed to add permission to group');
      }
    } catch (err) {
      setError('Error adding permission to group');
    }
  };

  const handleRemovePermissionFromGroup = async (groupId: number, permissionId: number) => {
    try {
      const response = await adminApi.removePermissionFromGroup(groupId, permissionId);
      if (response.success) {
        // Refresh groups to get updated data
        const groupsResponse = await adminApi.getGroups();
        if (groupsResponse.success) {
          setGroups(groupsResponse.data);
        }
        setSuccess('Permission removed from group successfully');
      } else {
        setError('Failed to remove permission from group');
      }
    } catch (err) {
      setError('Error removing permission from group');
    }
  };

  // Navigate to Permissions tab and highlight specific permission
  const handleNavigateToPermission = (permissionId: number) => {
    // Store the permission ID to highlight in the permissions page
    sessionStorage.setItem('highlightPermissionId', permissionId.toString());
    // Navigate to permissions page
    navigate('/permissions');
  };

  // New functions for multiple permission selection
  const handleOpenMultiplePermissionsDialog = (groupId: number) => {
    setCurrentGroupForMultiplePermissions(groupId);
    setSelectedPermissions([]);
    setOpenMultiplePermissionsDialog(true);
  };

  const handleCloseMultiplePermissionsDialog = () => {
    setOpenMultiplePermissionsDialog(false);
    setCurrentGroupForMultiplePermissions(null);
    setSelectedPermissions([]);
  };

  const handlePermissionSelectionChange = (permissionId: number, checked: boolean) => {
    if (checked) {
      setSelectedPermissions(prev => [...prev, permissionId]);
    } else {
      setSelectedPermissions(prev => prev.filter(id => id !== permissionId));
    }
  };

  const handleAddMultiplePermissionsToGroup = async () => {
    if (!currentGroupForMultiplePermissions || selectedPermissions.length === 0) {
      setError('Please select at least one redaction restriction');
      return;
    }

    try {
      // Add permissions one by one
      const promises = selectedPermissions.map(permissionId => 
        adminApi.addPermissionToGroup(currentGroupForMultiplePermissions, permissionId)
      );
      
      const results = await Promise.all(promises);
      const failedCount = results.filter(result => !result.success).length;
      
      if (failedCount === 0) {
        // Refresh groups to get updated data
        const groupsResponse = await adminApi.getGroups();
        if (groupsResponse.success) {
          setGroups(groupsResponse.data);
        }
        handleCloseMultiplePermissionsDialog();
        setSuccess(`${selectedPermissions.length} redaction restriction(s) added to group successfully`);
      } else {
        setError(`${failedCount} redaction restriction(s) failed to add`);
      }
    } catch (err) {
      setError('Error adding redaction restrictions to group');
    }
  };

  // Helper functions for select all/none
  const getAvailablePermissionsForGroup = (groupId: number) => {
    const group = groups.find(g => g.id === groupId);
    return permissions.filter(permission => !group?.permissions?.some(p => p.id === permission.id));
  };

  const handleSelectAllPermissions = () => {
    if (!currentGroupForMultiplePermissions) return;
    const availablePermissions = getAvailablePermissionsForGroup(currentGroupForMultiplePermissions);
    setSelectedPermissions(availablePermissions.map(p => p.id));
  };

  const handleDeselectAllPermissions = () => {
    setSelectedPermissions([]);
  };

  const filteredGroups = groups.filter(group =>
    group.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Group Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ borderRadius: 2 }}
        >
          Add Group
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Search Filter */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search groups by name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 400 }}
        />
        {searchQuery && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Showing {filteredGroups.length} of {groups.length} groups
          </Typography>
        )}
      </Box>

      {filteredGroups.length === 0 && searchQuery && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No groups found matching "{searchQuery}"
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {filteredGroups.map((group) => (
          <Accordion
            key={group.id}
            expanded={expandedGroups.has(group.id)}
            onChange={() => {
              setExpandedGroups(prev => {
                const newSet = new Set(prev);
                if (newSet.has(group.id)) {
                  newSet.delete(group.id);
                } else {
                  newSet.add(group.id);
                }
                return newSet;
              });
            }}
            sx={{ borderRadius: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                <GroupIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {group.name}
                </Typography>
                <Chip label={`ID: ${group.id}`} size="small" color="primary" variant="outlined" />
                
                {/* Status indicators */}
                {group.users && group.users.length > 0 && (
                  <Chip 
                    icon={<PersonIcon />} 
                    label={`${group.users.length} users`} 
                    size="small" 
                    color="success" 
                    variant="outlined" 
                  />
                )}
                {group.permissions && group.permissions.length > 0 && (
                  <Chip 
                    icon={<SecurityIcon />} 
                    label={`${group.permissions.length} redaction restrictions`} 
                    size="small" 
                    color="info" 
                    variant="outlined" 
                  />
                )}
                
                <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                  <IconButton
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenDialog(group);
                    }}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(group.id);
                    }}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                {/* Users Section */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon color="primary" />
                        Users ({group.users?.length || 0})
                      </Typography>
                      
                      {group.users && group.users.length > 0 ? (
                        <Box sx={{ 
                          maxHeight: 300, 
                          overflow: 'auto',
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                          mb: 2
                        }}>
                          <List dense>
                            {group.users.map((user) => (
                              <ListItem key={user.id}>
                                <ListItemText primary={user.email} />
                                <ListItemSecondaryAction>
                                  <IconButton
                                    edge="end"
                                    color="error"
                                    onClick={() => handleRemoveUserFromGroup(group.id, user.id)}
                                    size="small"
                                  >
                                    <RemoveIcon />
                                  </IconButton>
                                </ListItemSecondaryAction>
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      ) : (
                        <Box sx={{ 
                          p: 2, 
                          textAlign: 'center',
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                          mb: 2,
                          bgcolor: 'grey.50'
                        }}>
                          <Typography variant="body2" color="text.secondary">
                            No users assigned to this group
                          </Typography>
                        </Box>
                      )}
                      
                      <Divider sx={{ my: 2 }} />
                      
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Select User</InputLabel>
                          <Select
                            value={selectedUser}
                            label="Select User"
                            onChange={(e) => setSelectedUser(e.target.value as number | '')}
                          >
                            {users
                              .filter(user => !group.users?.some(u => u.id === user.id))
                              .map((user) => (
                                <MenuItem key={user.id} value={user.id}>
                                  {user.email}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleAddUserToGroup(group.id)}
                          disabled={!selectedUser}
                        >
                          Add
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Permissions Section */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SecurityIcon color="primary" />
                        Redaction Restrictions ({group.permissions?.length || 0})
                      </Typography>
                      
                      {group.permissions && group.permissions.length > 0 ? (
                        <Box sx={{ 
                          maxHeight: 300, 
                          overflow: 'auto',
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                          mb: 2
                        }}>
                          <List dense>
                            {group.permissions.map((permission) => (
                              <ListItem key={permission.id}>
                                <ListItemText 
                                  primary={permission.name}
                                  secondary={permission.description}
                                />
                                <ListItemSecondaryAction>
                                  <IconButton
                                    edge="end"
                                    color="primary"
                                    onClick={() => handleNavigateToPermission(permission.id)}
                                    size="small"
                                    title="View Redaction Restriction Details"
                                  >
                                    <OpenInNewIcon />
                                  </IconButton>
                                  <IconButton
                                    edge="end"
                                    color="error"
                                    onClick={() => handleRemovePermissionFromGroup(group.id, permission.id)}
                                    size="small"
                                  >
                                    <RemoveIcon />
                                  </IconButton>
                                </ListItemSecondaryAction>
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      ) : (
                        <Box sx={{ 
                          p: 2, 
                          textAlign: 'center',
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                          mb: 2,
                          bgcolor: 'grey.50'
                        }}>
                          <Typography variant="body2" color="text.secondary">
                            No redaction restrictions assigned to this group
                          </Typography>
                        </Box>
                      )}
                      
                      <Divider sx={{ my: 2 }} />
                      
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Single selection (existing) */}
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Select Redaction Restriction</InputLabel>
                            <Select
                              value={selectedPermission}
                              label="Select Redaction Restriction"
                              onChange={(e) => setSelectedPermission(e.target.value as number | '')}
                              MenuProps={{
                                PaperProps: {
                                  style: {
                                    maxHeight: 300,
                                  },
                                },
                              }}
                            >
                              {permissions
                                .filter(permission => !group.permissions?.some(p => p.id === permission.id))
                                .map((permission) => (
                                  <MenuItem key={permission.id} value={permission.id}>
                                    {permission.name}
                                  </MenuItem>
                                ))}
                            </Select>
                          </FormControl>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleAddPermissionToGroup(group.id)}
                            disabled={!selectedPermission}
                          >
                            Add
                          </Button>
                        </Box>
                        
                        {/* Multiple selection button */}
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleOpenMultiplePermissionsDialog(group.id)}
                          disabled={permissions.filter(permission => !group.permissions?.some(p => p.id === permission.id)).length === 0}
                          sx={{ alignSelf: 'flex-start' }}
                        >
                          Add Multiple Redaction Restrictions
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {/* Multiple Permissions Selection Dialog */}
      <Dialog 
        open={openMultiplePermissionsDialog} 
        onClose={handleCloseMultiplePermissionsDialog} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          Add Multiple Redaction Restrictions
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select the redaction restrictions you want to add to this group:
            </Typography>
            
            {/* Select All/None buttons */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleSelectAllPermissions}
                disabled={getAvailablePermissionsForGroup(currentGroupForMultiplePermissions || 0).length === 0}
              >
                Select All
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleDeselectAllPermissions}
                disabled={selectedPermissions.length === 0}
              >
                Deselect All
              </Button>
            </Box>
            
            <Box sx={{ 
              maxHeight: 400, 
              overflow: 'auto',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              p: 1
            }}>
              {getAvailablePermissionsForGroup(currentGroupForMultiplePermissions || 0).map((permission) => (
                <Box key={permission.id} sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
                  <Checkbox
                    checked={selectedPermissions.includes(permission.id)}
                    onChange={(e) => handlePermissionSelectionChange(permission.id, e.target.checked)}
                    size="small"
                  />
                  <Box sx={{ ml: 1, flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {permission.name}
                    </Typography>
                    {permission.description && (
                      <Typography variant="caption" color="text.secondary">
                        {permission.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
            
            {selectedPermissions.length > 0 && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                <Typography variant="body2" color="primary.contrastText">
                  Selected: {selectedPermissions.length} redaction restriction(s)
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMultiplePermissionsDialog}>Cancel</Button>
          <Button 
            onClick={handleAddMultiplePermissionsToGroup} 
            variant="contained"
            disabled={selectedPermissions.length === 0}
          >
            Add {selectedPermissions.length > 0 ? `(${selectedPermissions.length})` : ''} Redaction Restrictions
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingGroup ? 'Edit Group' : 'Add New Group'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Group Name"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={(e) => setFormData({ name: e.target.value })}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingGroup ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Groups; 